import { useState, useRef, useEffect } from "react";
import { fetchSecureBlobUrl } from "../../../hooks/useSecureVideoStream";

export interface AccordionItemData {
  id: string;
  title: string;
  description: string;
  linkText?: string;
  linkUrl?: string;
  bgColor: string;
  accentColor: string;
  videoUrl?: string;
}

const DEFAULT_ITEMS: AccordionItemData[] = [
  {
    id: "healthcare-ai",
    title: "Healthcare & Medical AI",
    description:
      "DICOM-native validation for radiology, pathology, and clinical AI. Board-certified radiologists review model outputs with multi-reviewer consensus and automated agreement scoring.",
    linkText: "Learn more",
    linkUrl: "/docs",
    bgColor: "#EAE6E1",
    accentColor: "#3d3d3d",
    videoUrl: "/static/videos/1.mp4",
  },
  {
    id: "nlp-language",
    title: "NLP & Language AI",
    description:
      "Validate NER extractions, structured data outputs, and multilingual AI responses. Side-by-side comparison of AI-generated vs. source text with field-level accuracy tracking.",
    linkText: "Learn more",
    linkUrl: "/docs",
    bgColor: "#EAF2E1",
    accentColor: "#558b2f",
    videoUrl: "/static/videos/2.mp4",
  },
  {
    id: "computer-vision",
    title: "Computer Vision",
    description:
      "Multi-reviewer validation for object detection, segmentation, and classification models. Independent expert review ensures your training data meets production quality standards.",
    linkText: "Learn more",
    linkUrl: "/docs",
    bgColor: "#D1D6D8",
    accentColor: "#475569",
    videoUrl: "/static/videos/3.mp4",
  },
  {
    id: "document-ai",
    title: "Document & OCR AI",
    description:
      "Validate document extraction, form parsing, and OCR outputs. Structured field-by-field verification with consensus scoring across multiple expert reviewers.",
    linkText: "Learn more",
    linkUrl: "/docs",
    bgColor: "#DCE5EA",
    accentColor: "#0284c7",
    videoUrl: "/static/videos/4.mp4",
  },
  {
    id: "llm-evaluation",
    title: "LLM & Generative AI",
    description:
      "Evaluate LLM responses for accuracy, safety, and alignment. Preference ranking, hallucination detection, and response quality scoring by domain experts.",
    linkText: "Learn more",
    linkUrl: "/docs",
    bgColor: "#E6DDD3",
    accentColor: "#78350f",
    videoUrl: "/static/videos/5.mp4",
  },
];


export interface HorizontalAccordionSectionProps {
  items?: AccordionItemData[];
  defaultOpenIndex?: number;
  className?: string;
  onItemHover?: (index: number) => void;
}

interface SecureAccordionVideoProps {
  src: string;
  isActive: boolean;
  sectionInView: boolean;
}

/**
 * SecureAccordionVideo:
 * 1. Streams videos via in-memory protected Blob URLs (no raw MP4 paths exposed).
 * 2. Caches chunks via CacheStorage (0 repeated network download).
 * 3. Only decodes when isActive && sectionInView, freeing 100% of decoder context on mobile devices.
 * 4. Disables right-click, PiP, and drag downloading.
 */
const SecureAccordionVideo: React.FC<SecureAccordionVideoProps> = ({
  src,
  isActive,
  sectionInView,
}) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);

  // Pre-fetch secure blob URL on mount so playback is immediate on hover/click
  useEffect(() => {
    let isCancelled = false;
    fetchSecureBlobUrl(src).then((url) => {
      if (!isCancelled) {
        setBlobUrl(url);
      }
    });
    return () => {
      isCancelled = true;
    };
  }, [src]);

  // Handle Play/Pause based on Active State AND Section In View
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !blobUrl) return;

    if (video.src !== blobUrl) {
      video.src = blobUrl;
    }

    if (isActive && sectionInView) {
      const promise = video.play();
      if (promise !== undefined) {
        promise.catch(() => {});
      }
    } else {
      video.pause();
    }
  }, [isActive, sectionInView, blobUrl]);

  return (
    <video
      ref={videoRef}
      muted
      loop
      playsInline
      preload="auto"
      disablePictureInPicture
      controlsList="nodownload nofullscreen noremoteplayback"
      onContextMenu={(e) => e.preventDefault()}
      draggable={false}
      className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${
        isActive ? "opacity-100" : "opacity-40"
      }`}
      style={{
        mixBlendMode: "multiply",
        pointerEvents: "none",
        userSelect: "none",
      }}
    />
  );
};

export const HorizontalAccordionSection = ({
  items = DEFAULT_ITEMS,
  defaultOpenIndex = 1, // Only the 2nd is open by default (index 1: Robotics & Humanoids)
  className = "",
  onItemHover,
}: HorizontalAccordionSectionProps) => {
  const [activeIndex, setActiveIndex] = useState<number>(defaultOpenIndex);
  const sectionRef = useRef<HTMLElement | null>(null);
  const [isSectionInView, setIsSectionInView] = useState<boolean>(true);

  // Viewport intersection observer to cut device GPU/CPU load to 0% when offscreen
  useEffect(() => {
    const el = sectionRef.current;
    if (!el || !("IntersectionObserver" in window)) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsSectionInView(entry.isIntersecting);
      },
      { threshold: 0.05 }
    );
    observer.observe(el);

    return () => observer.disconnect();
  }, []);

  // Document visibility change listener to pause playback when user switches tab
  useEffect(() => {
    const handleVisibility = () => {
      if (document.hidden) {
        setIsSectionInView(false);
      } else if (sectionRef.current) {
        const rect = sectionRef.current.getBoundingClientRect();
        setIsSectionInView(rect.top < window.innerHeight && rect.bottom > 0);
      }
    };

    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, []);

  const handleCardHover = (index: number) => {
    if (index === activeIndex) return;
    setActiveIndex(index);
    onItemHover?.(index);
  };

  return (
    <section ref={sectionRef} className={`w-full py-20 md:py-28 bg-black relative z-10 ${className}`}>
      <div className="max-w-[1360px] mx-auto px-4 sm:px-6 lg:px-10">
        {/* Section Header */}
        <div className="flex flex-col gap-3 mb-10 md:mb-12">
          <h2 className="text-3xl md:text-5xl font-medium tracking-tight text-white max-w-2xl">
            Validation across every AI modality.
          </h2>
        </div>

        {/* Horizontal Expandable Deck */}
        <div className="flex flex-col md:flex-row gap-3 md:gap-3.5 h-[560px] md:h-[480px] lg:h-[500px] w-full">
          {items.map((item, index) => {
            const isActive = activeIndex === index;

            return (
              <div
                key={item.id}
                onMouseEnter={() => handleCardHover(index)}
                onClick={() => handleCardHover(index)}
                className={`relative rounded-[28px] md:rounded-[32px] overflow-hidden cursor-pointer select-none transition-all duration-500 ease-[cubic-bezier(0.25,1,0.5,1)] ${
                  isActive
                    ? "flex-[4.5] md:flex-[3.8] shadow-[0_20px_50px_rgba(0,0,0,0.5)]"
                    : "flex-[1] md:flex-[0.7] hover:opacity-95"
                }`}
                style={{
                  backgroundColor: item.bgColor,
                  color: "#1a1a1a",
                  minWidth: isActive ? "280px" : "64px",
                }}
              >
                {/* Background Video (Secure Streamed & 0% Inactive Load) */}
                {item.videoUrl && (
                  <SecureAccordionVideo
                    src={item.videoUrl}
                    isActive={isActive}
                    sectionInView={isSectionInView}
                  />
                )}

                {/* Subtle bottom gradient to guarantee clean text legibility over animation */}
                <div
                  className={`absolute inset-x-0 bottom-0 h-44 pointer-events-none transition-opacity duration-500 ${
                    isActive ? "opacity-100" : "opacity-0"
                  }`}
                  style={{
                    background: `linear-gradient(to top, ${item.bgColor} 0%, ${item.bgColor}cc 50%, transparent 100%)`,
                  }}
                />

                {/* COLLAPSED STATE: Vertical rotated title */}
                <div
                  className={`absolute inset-0 flex items-end justify-center pb-8 z-10 pointer-events-none transition-all duration-300 ${
                    isActive
                      ? "opacity-0 translate-y-4 pointer-events-none"
                      : "opacity-100 translate-y-0"
                  }`}
                >
                  <span
                    className="font-normal text-base md:text-lg tracking-tight whitespace-nowrap"
                    style={{
                      color: "#1a1a1a",
                      writingMode: "vertical-rl",
                      transform: "rotate(180deg)",
                    }}
                  >
                    {item.title}
                  </span>
                </div>

                {/* EXPANDED STATE: Title, Description, and Link */}
                <div
                  className={`absolute bottom-8 left-8 right-8 z-10 flex flex-col gap-3 transition-all duration-500 ${
                    isActive
                      ? "opacity-100 translate-y-0 pointer-events-auto delay-100"
                      : "opacity-0 translate-y-4 pointer-events-none"
                  }`}
                >
                  <h3
                    className="text-3xl md:text-4xl font-normal tracking-tight leading-tight"
                    style={{ color: "#1a1a1a" }}
                  >
                    {item.title}
                  </h3>
                  <p
                    className="text-sm md:text-base leading-relaxed max-w-lg"
                    style={{ color: "#383838" }}
                  >
                    {item.description}
                  </p>
                  <div className="pt-2">
                    <a
                      href={item.linkUrl || "#"}
                      className="inline-flex items-center gap-2 text-sm font-medium hover:opacity-75 transition-all group/cta"
                      style={{ color: "#1a1a1a" }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <span>{item.linkText || "Read more"}</span>
                      <span className="transition-transform group-hover/cta:translate-x-1.5 duration-200">
                        →
                      </span>
                    </a>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default HorizontalAccordionSection;
